<script lang="ts">
  import {beforeUpdate, onMount} from 'svelte'
  import { _ } from 'svelte-i18n'
  import Button from './Button.svelte'
  import type { Section } from '../interfaces/section'
  import type { Option } from '../interfaces/option'
  import {
    selectedOptions,
    filteredProductGroup,
    configurationProductIds,
    selectedSection,
    toast,
    shouldPerformScroll,
    selectedDetails,
    hasSpecialCase,
    configuration
  } from '../store'
  export let section: Section
  const API_URL = globalThis.API_URL
  const doubleRowLayout = ['color', 'handles', 'legs', 'worktops']
  const woodMaterialNames = ['naturaloak', 'naturalwalnut', 'smokeoak', 'whiteoak']
  const vassColors = ['ashgreen', 'ivorybeige', 'dustyrose', 'afternoonwhite', 'sundaygreige', 'amagergrey']
  const lalaxColors = ['feathergrey', 'ivorybeige', 'naturalwhite', 'ashgreen']
  const lalaxOnlyWidth = ['75cm', '225cm']
  const lalaxDisallowedStyles = ['spacerpanels', 'plain']
  const vassDisallowedStyles = ['spacerpanels', 'plain']
  const lalaxSpecificHandles = 'lalaxhandles'
  const vassSpecificHandles = 'vasshandles'
  const lalaxSpecificStyle = 'lalaxstyle'
  const vassSpecificStyle = 'vassstyle'
  const lalaxSpecific150Width = '150cmlalax'

  const selectOption = (section: Section, option: Option) => {
    // reset toast contents
    $toast.specialCaseContent = ''
    $toast.content = ''

    let hasSpecialCase = false

    selectedOptions.selectOption(section.id, option)

    //handle special cases
    // if model is samso no handles
    if (Object.values($selectedOptions).find(s => s.name.toLowerCase() === 'samsö')
        && (section.name.toLowerCase() === 'handles' || section.name.toLowerCase() === 'model')) {
      $toast.specialCaseContent = $_('SPECIAL_CASE_SAMSO_NO_HANDLES')
      hasSpecialCase = true
    }

    // if lalax model selected - we inform about special cases
    if (Object.values($selectedOptions).find(s => s.name.toLowerCase() === 'lalax' && section.name.toLowerCase() === 'model')) {
        $toast.specialCaseContent = $_('LALAX_MODEL_MESSAGE')
        hasSpecialCase = true
    }

    // if vass model selected - we inform about special cases
    if (Object.values($selectedOptions).find(s => s.name.toLowerCase() === 'vass' && section.name.toLowerCase() === 'model')) {
      $toast.specialCaseContent = $_('VASS_MODEL_MESSAGE')
      hasSpecialCase = true
    }

    // if model samsö + height 236, no wood
    if (Object.values($selectedOptions).find(s => s.name.toLowerCase() === 'samsö')
        && Object.values($selectedOptions).find(s => s.name.toLowerCase().replace(' ', '') === '236cm')
        && (section.name.toLowerCase() === 'height' || section.name.toLowerCase() === 'model')) {
      $toast.specialCaseContent = $_('SPECIAL_CASE_SAMSO_236_NO_WOOD')
      hasSpecialCase = true
    }

    if (Object.values($selectedOptions).find(s => woodMaterialNames.includes(s.name.toLowerCase().replace(' ', '')))
        && Object.values($selectedOptions).find(s => s.name.toLowerCase().replace(' ', '') === '236cm')
        && Object.values($selectedOptions).find(s => s.name.toLowerCase() === 'samsö')
        && (section.name.toLowerCase() === 'height' || section.name.toLowerCase() === 'color')) {
      $toast.specialCaseContent = $_('SPECIAL_CASE_236_WOOD_NO_SAMSO')
      hasSpecialCase = true
    }

    $toast.content = `${$_(section.name.toUpperCase().replaceAll(' ',''), {default: section.name})}: ${$_(option.name.toUpperCase().replaceAll(' ',''), {default: option.name})}`
    $toast.show = true

    // getSelectedOptionDetails(section, option)

    setTimeout(() => {
      $toast.show = false
      // $selectedSection = null
      // $shouldPerformScroll = true
    }, hasSpecialCase ? $toast.duration * 10 : $toast.duration)
  }

  $: isSelected = (sectionId: string, optionId: Option) => {
      return (
      $selectedOptions &&
      $selectedOptions[sectionId] &&
      $selectedOptions[sectionId] === optionId
    )
  }

  $: specialCase_vassColors = false
  $: specialCase_lalaxColors = false
  $: specialCase_lalaxOnlyWidth = false
  $: specialCase_lalaxDisabledWidth = false
  $: specialCase_lalaxStyle = false
  $: specialCase_vassStyle = false
  $: specialCase_lalaxHandles = false
  $: specialCase_vassHandles = false
  $: specialCase_samsoNoHandles = false
  $: specialCase_samso236NoWood = false
  $: specialCase_samsoWoodNo236 = false
  $: specialCase_height236WoodNoSamso = false

  $: if(section) {
    // reset all special cases if the section changes
    specialCase_vassColors = false
    specialCase_lalaxColors = false
    specialCase_lalaxOnlyWidth = false
    specialCase_lalaxDisabledWidth = false
    specialCase_lalaxStyle = false
    specialCase_vassStyle = false
    specialCase_lalaxHandles = false
    specialCase_vassHandles = false
    specialCase_samsoNoHandles = false
    specialCase_samso236NoWood = false
    specialCase_samsoWoodNo236 = false
    specialCase_height236WoodNoSamso = false
  }

  const specialCaseSamsoNoHandles = (section: Section) => {
    // if samsö is chosen, only 'no handles' option is available in the handles section
    return section.name.toLowerCase() === 'handles' && Object.values($selectedOptions).find(s => s.name.toLowerCase() === 'samsö')
  }

  const specialCaseVassColors = (section: Section) => {
    // if Vass selected, only six colors are available
    return section.name.toLowerCase() === 'color' && Object.values($selectedOptions).find(s => s.name.toLowerCase() === 'vass')
  }

  const specialCaseLalaxColors = (section: Section) => {
      // if lalax selected, only four colors are available
      return section.name.toLowerCase() === 'color' && Object.values($selectedOptions).find(s => s.name.toLowerCase() === 'lalax')
  }

  const specialCaseLalaxOnlyWidth = (section: Section) => {
      // only if lalax selected, options 75cm and 225cm should be available
      return section.name.toLowerCase() === 'width' && Object.values($selectedOptions).find(s => ['samsö', 'ensiö', 'ingarö', 'vass'].includes(s.name.toLowerCase()))
  }

  const specialCaseLalaxDisabledWidth = (section: Section) => {
      // if lalax selected, option 250 cm should not be available
      return section.name.toLowerCase() === 'width' && Object.values($selectedOptions).find(s => s.name.toLowerCase() === 'lalax')
  }

  const specialCaseLalaxStyle = (section: Section) => {
      // if lalax selected, spacer panels should not be available
      return section.name.toLowerCase() === 'style' && Object.values($selectedOptions).find(s => s.name.toLowerCase() === 'lalax')
  }
  
  const specialCaseLalaxHandles = (section: Section) => {
    // if lalax selected, lalax specific handles only should be available
    return section.name.toLowerCase() === 'handles' && Object.values($selectedOptions).find(s => s.name.toLowerCase() === 'lalax')
  }

  const specialCaseVassStyle = (section: Section) => {
    // if vass selected, styles should not be available
    return section.name.toLowerCase() === 'style' && Object.values($selectedOptions).find(s => s.name.toLowerCase() === 'vass')
  }

  const specialCaseVassHandles = (section: Section) => {
    // if vass selected, vass specific handles only should be available
    return section.name.toLowerCase() === 'handles' && Object.values($selectedOptions).find(s => s.name.toLowerCase() === 'vass')
  }

  const specialCaseSamso236Wood = (section: Section) => {
    // if Samsö and height 236 are chosen, wood color options are disabled
    return section.name.toLowerCase() === 'color'
    && Object.values($selectedOptions).find(s => s.name.toLowerCase() === 'samsö')
    && Object.values($selectedOptions).find(s => s.name.toLowerCase().replace(' ', '') === '236cm')
  }

  const specialCaseSamsoWoodNo236 = (section: Section) => {
    // if Samsö and a wood color are chosen, no height 236 is available
    return section.name.toLowerCase() === 'height'
    && Object.values($selectedOptions).find(s => woodMaterialNames.includes(s.name.toLowerCase().replace(' ', '')))
    && Object.values($selectedOptions).find(s => s.name.toLowerCase() === 'samsö')
  }

  const specialCaseHeight236WoodNoSamso = (section: Section) => {
    // if height 236 and a wood color are chosen, Samsö model is not available
    return section.name.toLowerCase() === 'model'
    && Object.values($selectedOptions).find(s => woodMaterialNames.includes(s.name.toLowerCase().replace(' ', '')))
    && Object.values($selectedOptions).find(s => s.name.toLowerCase().replace(' ', '') === '236cm')
  }
</script>

<div class="options-container">
  <div class="options-title-container">
    <!--
      make .options-section__title to be left aligned if enabled.
    <Button class="nav" icon on:click>{$_('BACK')}</Button>
    -->
    <h2 class="options-section__title">{$_(`${section.name.toUpperCase().replaceAll(' ','')}`, {default: section.name})}</h2>
  </div>
  <div class="options">
    {#each section.options.slice().sort((a, b) => {
      return (
        section.optionOrder.indexOf(a.id) -
        section.optionOrder.indexOf(b.id)
      );
    }).filter(o => {
      //remove evo tap when lagoon sink is selected
      if (Object.values($selectedOptions).find(s => s.name.toLowerCase() === 'lagoon') && o.name.toLowerCase().replace(' ', '').includes('evo')) {
        return false
      }
      //remove birillo tap when lake sink is selected
      if (Object.values($selectedOptions).find(s => s.name.toLowerCase() === 'lake') && o.name.toLowerCase().replace(' ', '').includes('birillo')) {
        return false
      }
      //remove low legs for 40 cm model
      if ($configuration.productType && $configuration.productType === 'bathroom' && parseInt($selectedDetails.height) === 40 && o.name.toLowerCase().replace(' ', '').includes('low')) {
        return false
      }
      //remove high legs for 60 cm model
      if ($configuration.productType && $configuration.productType === 'bathroom' && parseInt($selectedDetails.height) === 60 && o.name.toLowerCase().replace(' ', '').includes('high')) {
        return false
      }
      // remove lalax style product from style selection for not lalax model selected
      if (!Object.values($selectedOptions).find(s => s.name.toLowerCase() === 'lalax') && o.name.toLowerCase().replace(' ', '') === lalaxSpecificStyle) {
        return false
      }
      // remove vass style product from style selection for not vass model selected
      if (!Object.values($selectedOptions).find(s => s.name.toLowerCase() === 'vass') && o.name.toLowerCase().replace(' ', '') === vassSpecificStyle) {
        return false
      }
      //remove 150 cm option for lalax since lalax have it's specific 150cm option
      if (Object.values($selectedOptions).find(s => s.name.toLowerCase() === 'lalax') && o.name.toLowerCase().replace(' ', '') === '150cm') {
        return false
      }
      // remove lalax handles product from handles selection for not lalax model selected
      if (!Object.values($selectedOptions).find(s => s.name.toLowerCase() === 'lalax') && o.name.toLowerCase().replace(' ', '') === lalaxSpecificHandles) {
        return false
      }
      // remove vass handles product from handles selection for not vass model selected
      if (!Object.values($selectedOptions).find(s => s.name.toLowerCase() === 'vass') && o.name.toLowerCase().replace(' ', '') === vassSpecificHandles) {
        return false
      }
      // remove 150cm lalax product from handles selection for not lalax model selected
      if (!Object.values($selectedOptions).find(s => s.name.toLowerCase() === 'lalax') && o.name.toLowerCase().replaceAll(' ', '') === lalaxSpecific150Width) {
        return false
      }

      return true
    })
      .filter(o => {
      if(specialCaseSamsoNoHandles(section)) {
        specialCase_samsoNoHandles = true
        $hasSpecialCase = true
        // return o.name.toLowerCase() === 'no handles'
      }
      if (specialCaseVassColors(section)) {
        specialCase_vassColors = true
        $hasSpecialCase = true
      }
      if (specialCaseLalaxColors(section)) {
        specialCase_lalaxColors = true
        $hasSpecialCase = true
      }
      if (specialCaseLalaxOnlyWidth(section)) {
        specialCase_lalaxOnlyWidth = true
        $hasSpecialCase = true
      }
      if (specialCaseLalaxDisabledWidth(section)) {
        specialCase_lalaxDisabledWidth = true
        $hasSpecialCase = true
      }
      if (specialCaseLalaxStyle(section)) {
        specialCase_lalaxStyle = true
        $hasSpecialCase = true
      }
      if (specialCaseLalaxHandles(section)) {
        specialCase_lalaxHandles = true
        $hasSpecialCase = true
      }
      if (specialCaseVassStyle(section)) {
        specialCase_vassStyle = true
        $hasSpecialCase = true
      }
      if (specialCaseVassHandles(section)) {
        specialCase_vassHandles = true
        $hasSpecialCase = true
      }
      if(specialCaseSamso236Wood(section)) {
        specialCase_samso236NoWood = true
        $hasSpecialCase = true
      }
      if(specialCaseSamsoWoodNo236(section)) {
        specialCase_samsoWoodNo236 = true
        $hasSpecialCase = true
      }
      if(specialCaseHeight236WoodNoSamso(section)) {
        specialCase_height236WoodNoSamso = true
        $hasSpecialCase = true
      }
      return o
    }) as option}
      <div
        class="option
        {doubleRowLayout.includes(section.name.toLowerCase())
          ? 'double-row'
          : 'single-row'}
        {isSelected(section.id, option)
          ? 'selected'
          : ''}
        {specialCase_samsoNoHandles && option.name.toLowerCase().replace(' ',  '') !== 'nohandles'
          ? 'special-case-disabled-option'
          : ''}
        {specialCase_vassColors && !vassColors.includes(option.name.toLowerCase().replace(' ',  ''))
          ? 'special-case-disabled-option'
          : ''}
        {specialCase_lalaxColors && !lalaxColors.includes(option.name.toLowerCase().replace(' ',  ''))
          ? 'special-case-disabled-option'
          : ''}
        {specialCase_lalaxOnlyWidth && lalaxOnlyWidth.includes(option.name.toLowerCase().replace(' ',  ''))
          ? 'special-case-disabled-option'
          : ''}
        {specialCase_lalaxDisabledWidth && option.name.toLowerCase().replace(' ',  '') === '250cm'
          ? 'special-case-disabled-option'
          : ''}
        {specialCase_lalaxStyle && lalaxDisallowedStyles.includes(option.name.toLowerCase().replace(' ',  ''))
          ? 'special-case-disabled-option'
          : ''}
        {specialCase_vassStyle && vassDisallowedStyles.includes(option.name.toLowerCase().replace(' ',  ''))
          ? 'special-case-disabled-option'
          : ''}
        {specialCase_vassHandles && option.name.toLowerCase().replace(' ',  '') !== vassSpecificHandles
          ? 'special-case-disabled-option'
          : ''}
        {!specialCase_vassHandles && option.name.toLowerCase().replace(' ',  '') === vassSpecificHandles
          ? 'special-case-disabled-option'
          : ''}
        {specialCase_lalaxHandles && option.name.toLowerCase().replace(' ',  '') !== lalaxSpecificHandles
          ? 'special-case-disabled-option'
          : ''}
        {!specialCase_lalaxHandles && option.name.toLowerCase().replace(' ',  '') === lalaxSpecificHandles
          ? 'special-case-disabled-option'
          : ''}
        {specialCase_samso236NoWood && woodMaterialNames.includes(option.name.toLowerCase().replace(' ', ''))
          ? 'special-case-disabled-option'
          : ''}
        {specialCase_samsoWoodNo236 && option.name.toLowerCase().replace(' ',  '') === '236cm'
          ? 'special-case-disabled-option'
          : ''}
        {specialCase_height236WoodNoSamso && option.name.toLowerCase().replace(' ',  '') === 'samsö'
          ? 'special-case-disabled-option'
          : ''}
          "
        on:click={() => selectOption(section, option)}
      >
        <div class="option__image">
          <img src={option.photo
              ? `${API_URL}images/${option.photo}?size=original`
              : `images/default-img.png`}
            alt={section.name}
          />
        </div>
        <div class="option__name"><span class="clamp">{$_(`${option.name.toUpperCase().replaceAll(' ','')}`, {default: option.name})}</span></div>
      </div>
    {/each}
    {#if specialCase_samsoNoHandles && section.name.toLowerCase() === 'handles'}
    <div class="special-case-notification-container text-gray __configurator-desktop-flex">
      <p class="special-case-notification-text">{$_('SPECIAL_CASE_SAMSO_NO_HANDLES')}</p>
    </div>
    {:else if specialCase_samso236NoWood && section.name.toLowerCase() === 'color'}
      <div class="special-case-notification-container text-gray __configurator-desktop-flex">
      <p class="special-case-notification-text">{$_('SPECIAL_CASE_SAMSO_236_NO_WOOD')}</p>
    </div>
    {:else if specialCase_samsoWoodNo236 && section.name.toLowerCase() === 'height'}
    <div class="special-case-notification-container text-gray __configurator-desktop-flex">
      <p class="special-case-notification-text">{$_('SPECIAL_CASE_SAMSO_WOOD_NO_236')}</p>
    </div>
    {:else if specialCase_height236WoodNoSamso && section.name.toLowerCase() === 'model'}
    <div class="special-case-notification-container text-gray __configurator-desktop-flex">
      <p class="special-case-notification-text">{$_('SPECIAL_CASE_236_WOOD_NO_SAMSO')}</p>
    </div>
  {/if}
  </div>
  <div class="actions">
    <Button class="nav with-icon" centered icon on:click>{$_('BACK')}</Button>
  </div>
</div>
{#if specialCase_samsoNoHandles && section.name.toLowerCase() === 'handles'}
    <div class="special-case-notification-container text-gray __configurator-mobile-flex">
      <p class="special-case-notification-text">{$_('SPECIAL_CASE_SAMSO_NO_HANDLES')}</p>
    </div>
    {:else if specialCase_samso236NoWood && section.name.toLowerCase() === 'color'}
    <div class="special-case-notification-container text-gray __configurator-mobile-flex">
      <p class="special-case-notification-text">{$_('SPECIAL_CASE_SAMSO_236_NO_WOOD')}</p>
    </div>
    {:else if specialCase_samsoWoodNo236 && section.name.toLowerCase() === 'height'}
    <div class="special-case-notification-container text-gray __configurator-mobile-flex">
      <p class="special-case-notification-text">{$_('SPECIAL_CASE_SAMSO_WOOD_NO_236')}</p>
    </div>
    {:else if specialCase_height236WoodNoSamso && section.name.toLowerCase() === 'model'}
    <div class="special-case-notification-container text-gray __configurator-mobile-flex">
      <p class="special-case-notification-text">{$_('SPECIAL_CASE_236_WOOD_NO_SAMSO')}</p>
    </div>
  {/if}

<style>
  .options-container {
    display: flex;
    height: 100%;
    flex-flow: column;
    /* height calculated by deducting the height of the ConfigurationProductInfo.svelte component height and shop (theme) navigation bar*/
    max-height: calc(100vh - 184px - 74px);
    max-width: 433px;
  }

  .options-title-container {
    border-bottom: solid 1px;
    border-bottom-color: var(--color-light-gray);
    margin: 0 32px;
    padding-bottom: 16px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }

  .options-title-container .options-section__title {
    line-height: 1;
    text-align: center;
    color: var(--color-medium-gray);
    flex: 1 0 auto;
    font-size: 1.2em;
  }

  .options {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    overflow-y: auto;
    align-items: start;
    /* max-height: 700px; */
    /* border-bottom: 1px solid var(--color-light-gray); */
  }

  .option__image {
    display: grid;
    place-content: center;
  }

  .option {
    cursor: pointer;
    display: flex;
    flex-flow: column;
    /* justify-content: space-between; */
    align-items: center;
    padding: 16px 0;
    text-align: center;
    /* Fit option container if only one option is available e.g. Samsö + no handles */
    height: fit-content;
  }

  .option.single-row {
    width: 100%;
  }

  .option.double-row {
    width: 50%;
  }

  .option.single-row img {
    max-width: 325px;
  }

  .option.double-row img {
    max-width: 162px;
  }

  .selected {
    background: var(--color-selected-gray);
  }

  .actions {
    height: 50px;
    min-height: 50px;
    display: flex;
    justify-content: center;
    border-top: solid 1px;
    border-top-color: var(--color-light-gray);
    padding: 0 32px;
  }

  .clamp {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2; /* number of lines to show */
    -webkit-box-orient: vertical;
  }

  @media screen and (min-width: 1025px) {
    .option__name {
      max-width: 175px;
      margin-top: 8px;
    }

    .clamp {
      -webkit-line-clamp: 1; /* number of lines to show */
    }
    .actions {
      margin-top: auto;
    }
    .option.double-row .option__image {
      height: 120px;
    }
  }

  .special-case-disabled-option {
    opacity: 0.1;
    pointer-events: none;
  }

  .special-case-notification-container {
    display: flex;
    justify-content: center;
    padding: 32px;
    width: 100%;
    /* background-color: var(--color-selected-gray); */
  }

  .special-case-notification-text {
    display: flex;
    text-align: center;
    align-items: center;
    margin: 0;
  }

  @media screen and (max-width: 1024px) {
    .options-title-container,
    .actions {
      display: none;
    }

    .option__image {
      height: 60px;
    }

    .options-container {
      display: flex;
      flex-direction: row;
      max-width: none;
      /* border-bottom: 1px solid #eeeff0; */
    }

    .options {
      flex-wrap: nowrap;
      border: none;
      height: fit-content;
      align-items: center;
    }

    .option {
      justify-content: flex-start;
      /* padding: 8px 16px; */
      /* height: 150px !important; */
      min-width: 125px !important;
      padding:  0;
      min-height: 128px;
      height: 100%;
      padding-top: 12px;
    }

    .option.single-row,
    .option.double-row {
      width: auto;
      /* max-height: 120px; */
    }

    .option img {
      width: 50px;
      height: auto;
      max-height: 50px;
    }

    .option__name {
      height: 36px;
      max-width: 90px;
      margin-top: 6px;
      line-height: normal;

      display: grid;
      place-content: center;
    }

    .actions {
      display: none;
    }

    .special-case-notification-container {
      border-top: 1px solid #eeeff0;
      padding: 0 18px;
      min-height: 71px;
    }
  }
</style>
